const swc = ((navigator as Navigator).serviceWorker as ServiceWorkerContainer);
import SignalsService from '../services/core/SignalsService';
import { IAuthenticationBridgeClient } from './IAuthenticationBridgeClient';
import AuthenticatedUserModel from '../../../../serviceworker/src/models/AuthenticatedUserModel';
import * as Sentry from "@sentry/browser";
import { tracker as openReplayTracker } from '@openreplay/tracker';
import { clearSessionResumeStorage } from '../utils/sessionResumeStorage';
export default class PWAAuthenticationBridgeClient implements IAuthenticationBridgeClient {

    private signalsService: SignalsService;
    
    authenticatedUserData: AuthenticatedUserModel | null = null;
    authenticatedInvigilatorData: AuthenticatedUserModel | null = null;
    constructor(signalsService: SignalsService) {
        this.signalsService = signalsService;
        this.initHandlers();
    }

    private initHandlers() {
        swc.addEventListener('message', (event) => {
            switch (event.data.type) {
                case 'tryUnlockSuccess':
                    this.handleUnlockSucccess(event);
                    break;
                case 'tryUnlockFailed':
                    this.handleUnlockFailed(event);
                    break;
                case 'tryInvigilatorAuthenticationSuccess':
                    this.handleInvigilatorAuthenticationSuccess(event);
                    break;
                case 'tryInvigilatorAuthenticationFailed':
                    this.handleInvigilatorAuthenticationFailed(event);
                    break;
                case 'serviceWorkerReset':
                    this.handleResetServiceWorkerReset(event);
                    break;
            }
        });
    }

    private handleResetServiceWorkerReset(message: MessageEvent<any>) {
        // Check if this is a health check recovery - if so, preserve session resume data
        // so that SessionResumeService can restore the session after reload
        const isHealthCheckRecovery = sessionStorage.getItem('healthCheckRecovery') === 'true';

        console.log("AuthenticationBridgeClient(PWA) SW reset event - isHealthCheckRecovery:", isHealthCheckRecovery);

        // Always clear auth data from memory and storage
        this.authenticatedUserData = null;
        this.authenticatedInvigilatorData = null;
        sessionStorage.removeItem('authenticatedUserData');
        sessionStorage.removeItem('authenticatedInvigilatorData');

        // Only clear session resume data if this is NOT a health check recovery
        // During health check recovery, we need this data to restore the session
        if (!isHealthCheckRecovery) {
            console.log("AuthenticationBridgeClient(PWA) Intentional reset - clearing session resume data");
            clearSessionResumeStorage();
        } else {
            console.log("AuthenticationBridgeClient(PWA) Health check recovery - PRESERVING session resume data");
            // Clear the recovery flag - it has served its purpose
            sessionStorage.removeItem('healthCheckRecovery');
        }
    }

    private handleUnlockSucccess(message: MessageEvent<any>) {
        console.log("AuthenticationBridgeClient(PWA) handleUnlockSucccess");
        if (!('authenticatedUser' in message.data)) {
            console.error("AuthenticationBridgeClient(PWA) handleUnlockSucccess: No authenticatedUser in message", message);
            return;
        }
        this.authenticatedUserData = new AuthenticatedUserModel(message.data.authenticatedUser._userUUID, message.data.authenticatedUser.userData);
        let uuid = this.authenticatedUserData.userUUID;
        if (!uuid) {
            uuid = this.authenticatedUserData.userData._userUUID;
        }
        // Store authenticated user data in sessionStorage
        sessionStorage.setItem('authenticatedUserData', JSON.stringify(this.authenticatedUserData));

        // Store session resume key now that unlock succeeded (for kiosk mode session recovery)
        if (this.pendingUnlockKey) {
            sessionStorage.setItem('sessionResumeKey', this.pendingUnlockKey);
            sessionStorage.setItem('sessionResumeTimestamp', Date.now().toString());
            this.pendingUnlockKey = null;
        }

        this.signalsService.emit("tryUnlockSuccess", uuid);
        Sentry.addBreadcrumb({
            category: 'authentication',
            message: 'User unlocked',
            data: {
                uuid: uuid
            }
        });
        Sentry.setUser({
            id: uuid            
        });
        openReplayTracker.setMetadata("userUUID", uuid);
        openReplayTracker.setUserID(uuid);
    }

    private handleUnlockFailed(message:MessageEvent<any>) {
        console.log("AuthenticationBridgeClient(PWA) handleUnlockFailed");
        this.pendingUnlockKey = null;
        this.signalsService.emit("tryUnlockFailed");
        this.authenticatedUserData = null;
    }

    private handleInvigilatorAuthenticationSuccess(message: MessageEvent<any>) {
        console.log("AuthenticationBridgeClient(PWA) handleInvigilatorAuthenticationSuccess");
        if (!('authenticatedUser' in message.data)) {
            console.error("AuthenticationBridgeClient(PWA) handleInvigilatorAuthenticationSuccess: No authenticatedUser in message", message);
            return;
        }
        this.authenticatedInvigilatorData = new AuthenticatedUserModel(message.data.authenticatedUser._userUUID, message.data.authenticatedUser._userData);
        // Store authenticated user data in sessionStorage
        sessionStorage.setItem('authenticatedInvigilatorData', JSON.stringify(this.authenticatedInvigilatorData));
        this.signalsService.emit("tryInvigilatorAuthenticationSuccess", JSON.stringify(this.authenticatedInvigilatorData?.userData));
    }

    private handleInvigilatorAuthenticationFailed(message: MessageEvent<any>) {
        console.log("AuthenticationBridgeClient(PWA) handleInvigilatorAuthenticationFailed");
        this.signalsService.emit("tryInvigilatorAuthenticationFailed");
        this.authenticatedUserData = null;
    }

    // Temporarily store the key while unlock is in progress
    private pendingUnlockKey: string | null = null;

    public tryUnlock(concatenatedKey:string): void {
        console.log("AuthenticationBridgeClient(PWA)", concatenatedKey);
        const msg = {
            type: "tryUnlock",
            concatenatedKey: concatenatedKey
        };

        if (!swc.controller) {
            console.error("No service worker controller available");
            this.handleUnlockFailed(new MessageEvent('error'));
            return;
        }

        // Store key temporarily - will be persisted only on successful unlock
        this.pendingUnlockKey = concatenatedKey;

        swc.controller.postMessage(msg);
    }

    public isUnlocked(): boolean {
        return this.authenticatedUserData !== null;
    }

    public getUserUUID(): string | null {
        if (this.authenticatedUserData !== null) {
            return this.authenticatedUserData.userUUID;
        }
        return null;
    }

    // Add a method to restore authenticated user from sessionStorage
    public restoreAuthenticatedUser(): AuthenticatedUserModel | null {
        const data = sessionStorage.getItem('authenticatedUserData');
        if (data) {
            this.authenticatedUserData = JSON.parse(data) as AuthenticatedUserModel;
            return this.authenticatedUserData;
        }
        return null;
    }

    // Get stored concatenatedKey for session resume
    public getStoredSessionResumeKey(): string | null {
        return sessionStorage.getItem('sessionResumeKey');
    }

    // Check if session resume data is available
    public hasSessionResumeData(): boolean {
        return sessionStorage.getItem('sessionResumeKey') !== null &&
               localStorage.getItem('sessionResumeAssessmentId') !== null;
    }

    // Clear session resume data (call after successful resume or on explicit logout)
    public clearSessionResumeData(): void {
        clearSessionResumeStorage();
    }

    public logout(): void {
        // Clear authenticated user data
        this.authenticatedUserData = null;
        sessionStorage.removeItem('authenticatedUserData');
        this.clearSessionResumeData();
        this.signalsService.emit("logoutSuccess");
    }

    public tryInvigilatorAuthentication(concatenatedKey: string): void {
        console.log("AuthenticationBridgeClient(PWA) tryInvigilatorAuthentication", concatenatedKey);
        const msg = {
            type: "tryInvigilatorUnlock",
            concatenatedKey: concatenatedKey
        };
        
        if (!swc.controller) {
            console.error("No service worker controller available");
            this.handleInvigilatorAuthenticationFailed(new MessageEvent('error'));
            return;
        }
        
        swc.controller.postMessage(msg);
    }

} // class AuthenticationBridgeClient
